import axios from 'axios';

const API_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

export interface InventorySummary {
  totalProducts: number;
  totalValue: number;
  totalCost: number;
  totalProfit: number;
  lowStockProducts: number;
  outOfStockProducts: number;
  averageStockDays: number;
  categoryCount: number;
}

export interface TopSellingProduct {
  productId: number;
  productName: string;
  categoryName: string;
  totalQuantitySold: number;
  totalRevenue: number;
  currentStock: number;
}

export interface LowStockProduct {
  productId: number;
  productName: string;
  categoryName: string;
  currentStock: number;
  minStockThreshold: number;
  stockDeficit: number;
  price: number;
  status: string;
}

export interface InventoryValuation {
  productId: number;
  productName: string;
  categoryName: string;
  currentStock: number;
  costPrice: number;
  salePrice: number;
  totalCost: number;
  totalValue: number;
  profitMargin: number;
}

export interface ProductMovement {
  productId: number;
  productName: string;
  categoryName: string;
  currentStock: number;
  totalSold: number;
  averageDailySales: number;
  daysOfStockRemaining: number;
  lastSaleDate?: string;
}

const inventoryReportsService = {
  getSummary: async (): Promise<InventorySummary> => {
    const response = await axios.get(`${API_URL}/inventoryreports/summary`);
    return response.data;
  },

  getTopSellingProducts: async (startDate?: string, endDate?: string, limit: number = 10): Promise<TopSellingProduct[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('limit', limit.toString());
    
    const response = await axios.get(`${API_URL}/inventoryreports/top-selling?${params}`);
    return response.data;
  },

  getLowStockProducts: async (): Promise<LowStockProduct[]> => {
    const response = await axios.get(`${API_URL}/inventoryreports/low-stock`);
    return response.data;
  },

  getInventoryValuation: async (): Promise<InventoryValuation[]> => {
    const response = await axios.get(`${API_URL}/inventoryreports/valuation`);
    return response.data;
  },

  getProductMovements: async (startDate?: string, endDate?: string): Promise<ProductMovement[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await axios.get(`${API_URL}/inventoryreports/movements?${params}`);
    return response.data;
  },

  exportTopSellingProducts: async (startDate?: string, endDate?: string, limit: number = 10): Promise<void> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    params.append('limit', limit.toString());
    
    const response = await axios.get(`${API_URL}/inventoryreports/export/top-selling?${params}`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Productos_mas_vendidos_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  exportLowStockProducts: async (): Promise<void> => {
    const response = await axios.get(`${API_URL}/inventoryreports/export/low-stock`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Productos_bajo_stock_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  exportInventoryValuation: async (): Promise<void> => {
    const response = await axios.get(`${API_URL}/inventoryreports/export/valuation`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Valorizacion_inventario_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  },

  exportProductMovements: async (startDate?: string, endDate?: string): Promise<void> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await axios.get(`${API_URL}/inventoryreports/export/movements?${params}`, {
      responseType: 'blob'
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Movimientos_productos_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
};

export default inventoryReportsService;
